import type {
  CoachingSession,
  CoachingSessionStatus,
  CoachingSessionType,
} from '@efdigital/ll-addon-data';
import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import { jsonApiOptions } from '@efdigital/ll-admin-v2/lib/json-api.ts';
import { findRecord, query } from '@warp-drive/utilities/json-api';

import { include, page, sort } from '../../json-api.ts';

/**
 * Scope for a session list. Every filter is optional, so the same query serves
 * the unfiltered admin list and each in-context list (a coach's sessions, a
 * partnership's history, a plan's sessions).
 */
export interface CoachingSessionFilters {
  sessionType?: CoachingSessionType | '';
  statuses?: CoachingSessionStatus[];
  /** Sessions assigned to this coach. */
  coachUserId?: string;
  /** Sessions belonging to this coachee. */
  coacheeUserId?: string;
  /** Sessions under one coach/coachee partnership. */
  partnershipId?: string;
  /** Sessions under one coachee's coaching plan. */
  planId?: string;
}

const SESSION_INCLUDES = ['coachingPartnership', 'coachingPlan'];

function sessionFilterParams(filters: CoachingSessionFilters) {
  return {
    ...(filters.sessionType
      ? { 'filter[sessionType]': filters.sessionType }
      : {}),
    ...(filters.statuses?.length
      ? { 'filter[status][$in][]': filters.statuses }
      : {}),
    ...(filters.coachUserId
      ? { 'filter[coachUserId]': filters.coachUserId }
      : {}),
    ...(filters.coacheeUserId ? { 'filter[user]': filters.coacheeUserId } : {}),
    ...(filters.partnershipId
      ? { 'filter[coachingPartnership.id]': filters.partnershipId }
      : {}),
    ...(filters.planId ? { 'filter[coachingPlan.id]': filters.planId } : {}),
  };
}

/**
 * The single session query used by every session list in the admin app. Callers
 * supply whatever scope applies and the pagination window; the sort, includes
 * and filter encoding stay in one place so the lists cannot drift apart.
 */
export function queryCoachingSessions(
  filters: CoachingSessionFilters,
  { offset, limit }: { offset: number; limit: number }
) {
  return query<CoachingSession>(
    DataTypeCoaching.CoachingSession,
    {
      ...sessionFilterParams(filters),
      ...page({ offset, limit }),
      ...include(SESSION_INCLUDES),
      ...sort('-modified'),
    },
    jsonApiOptions
  );
}

export function findCoachingSession(id: string) {
  return findRecord<CoachingSession>(DataTypeCoaching.CoachingSession, id, {
    ...jsonApiOptions,
    include: [
      'coachingPartnership',
      'coachingPlan',
      'coachingPlan.coaching',
      'coachingPlan.coaching.coachingConfig',
    ],
  });
}
